import React, { useState, useEffect } from 'react';
import moment from 'moment-timezone';
import styles from './Chart.module.css';
import { Row, Container, Col } from "reactstrap";
import { FaInfoCircle } from "react-icons/fa";
import LstmModelInfo from "./Info/LstmModelInfo";
import { useAppContext } from "../../App"; // Import AppContext


function LSTMPredictChart() {


  const {
    availableModels, availableModels2, selectedModel, setSelectedModel, selectedModel2, setSelectedModel2, timeZone, handleCreateModel, handleCreateModel2, currencyPair,
    setCurrencyPair, message, setMessage, g50Currencies, Plot, Dropdown
  } = useAppContext(); // Destructure values from AppContext

  const [predictedPrices, setPredictedPrices] = useState([]); // Initialize as an empty array
  const [predictedPrices2, setPredictedPrices2] = useState([]); // Initialize as an empty array
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false); // Add loading state for button feedback
  const [currentModelForPrediction, setCurrentModelForPrediction] = useState(null); // Track the model used for the chart
  const [currentModelForPrediction2, setCurrentModelForPrediction2] = useState(null); // Track the model used for the chart
  const [isInfoOpen, setIsInfoOpen] = useState(false); // Manage FvgInfo state
  const toggleInfoDrawer = () => {
    setIsInfoOpen((prev) => !prev); // Toggle FvgInfo drawer
  };
  // Filter currency options to exclude those already in available models
  const filteredCurrencyOptions = g50Currencies.filter(
    (currency) => !availableModels.includes(currency.pair)
  ).map((currency) => ({
    label: `${currency.country} (${currency.pair})`,
    value: currency.pair,
  }));
  const filteredCurrencyOptions2 = g50Currencies.filter(
    (currency) => !availableModels2.includes(currency.pair)
  ).map((currency) => ({
    label: `${currency.country} (${currency.pair})`,
    value: currency.pair,
  }));

  // Function to fetch predictions
  const fetchPrediction = async (model) => {
    if (!model) return; // Do nothing if no model is selected
    try {
      setError(null); // Clear any previous errors
      setLoading(true); // Set loading state
      setMessage(`Model 1 Prediction Processing for ${model}`); // Update message dynamically
      const response = await fetch('https://vidplay-api.deng-software.com/flask/predict2', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currencyPair: model }), // Ensure proper key format
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Update the predicted prices
      if (data.predicted_prices) {
        const currentTime = moment().tz(timeZone); // Ensure valid moment object
        if (!currentTime || !currentTime.isValid()) {
          throw new Error('Invalid current time for timezone');
        }

        const times = Array.from({ length: 10 }, (_, i) =>
          currentTime.clone().add(i + 1, 'minutes').format('YYYY-MM-DD HH:mm:ss')
        );

        setPredictedPrices(
          times.map((time, index) => ({ time, price: data.predicted_prices[index] }))
        );

        // Update the model used for the chart only after prediction data is returned
        setCurrentModelForPrediction(model);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false); // Reset loading state
      setMessage(`Model 1 Prediction Complete for ${model}`); // Update message dynamically
    }
  };

  // Function to fetch predictions
  const fetchPrediction2 = async (model) => {
    if (!model) return; // Do nothing if no model is selected
    try {
      setError(null); // Clear any previous errors
      setLoading(true); // Set loading state
      setMessage(`Model 2 Prediction Processing for ${model}`); // Update message dynamically
      const response = await fetch('https://vidplay-api.deng-software.com/flask/predict3', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ currencyPair: model }), // Ensure proper key format
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Update the predicted prices
      if (data.predicted_prices) {
        const currentTime = moment().tz(timeZone); // Ensure valid moment object
        if (!currentTime || !currentTime.isValid()) {
          throw new Error('Invalid current time for timezone');
        }

        const times = Array.from({ length: 10 }, (_, i) =>
          currentTime.clone().add(i + 1, 'minutes').format('YYYY-MM-DD HH:mm:ss')
        );

        setPredictedPrices2(
          times.map((time, index) => ({ time, price: data.predicted_prices[index] }))
        );

        // Update the model used for the chart only after prediction data is returned
        setCurrentModelForPrediction2(model);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false); // Reset loading state
      setMessage(`Model 2 Prediction Complete for ${model}`); // Update message dynamically
    }
  };



  // Trigger prediction when the selected model changes
  useEffect(() => {
    fetchPrediction(selectedModel);
  }, [selectedModel]); // Dependency: Fetch predictions whenever selectedModel changes

  // Trigger prediction when the selected model changes
  useEffect(() => {
    fetchPrediction2(selectedModel2);
  }, [selectedModel2]); // Dependency: Fetch predictions whenever selectedModel changes

  // Ensure the first available currency is pre-selected after filtering
  useEffect(() => {
    if (!currencyPair && filteredCurrencyOptions.length > 0) {
      setCurrencyPair(filteredCurrencyOptions[0].value);
    }
  }, [filteredCurrencyOptions, currencyPair, setCurrencyPair]);

  return (
    <>
      <Container><Row>
        <Row>
          <div className={styles.chartContainer}>
            {/* Header with Info Icon */}
            <div className={styles.header}>
              <h2 className={styles.chartTitle}>
                LSTM Prediction Model
                <FaInfoCircle
                  onClick={toggleInfoDrawer}
                  style={{
                    marginLeft: "20px",
                    cursor: "pointer",
                    fontSize: "20px",
                    verticalAlign: "middle",
                  }}
                  title="Open Liquidity Sweep  Info" // Tooltip for accessibility
                />
              </h2>
            </div>
            <LstmModelInfo isOpen={isInfoOpen} toggleDrawer={toggleInfoDrawer} />

          </div>
        </Row>
        <Row>
          <h3 className={styles.chartTitle}>Open, Close, High, Low</h3>
        </Row>
        <Row>
          <Col md={4}>
            {/* Dropdown for selecting models */}
            <Dropdown
              label="Select Currency To Predict (Model 1)"
              options={availableModels.map((model) => ({
                label: model,
                value: model,
              }))}
              value={selectedModel}
              onChange={setSelectedModel}
            />
          </Col>
          <Col md={4}>
            {/* Dropdown for creating a new model */}
            <div className={styles.createModelSection}>
              <Dropdown
                label="Select New Currency Model to Generate"
                options={filteredCurrencyOptions}
                value={currencyPair}
                onChange={(value) => setCurrencyPair(value)}
              />
            </div>
          </Col>
          <Col md={4} className={styles.createModelSection}>
            <button
              onClick={() => handleCreateModel(currencyPair || filteredCurrencyOptions[0]?.value)}
              disabled={loading || filteredCurrencyOptions.length === 0}
            >
              {loading
                ? 'Creating Model...'
                : `Create Model 1 for ${currencyPair || filteredCurrencyOptions[0]?.value}`}
            </button>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            {message && <p className={styles.message}>{message}</p>}
            {selectedModel && !loading && (
              <>
                <h3 className={styles.chartTitle}>Next 10-Minute Prediction for {currentModelForPrediction}</h3>
                {error && <p className={styles.error}>Error: {error}</p>}
              </>
            )}
          </Col>
        </Row>
        <Row><Col md={12}>
          {predictedPrices.length > 0 && currentModelForPrediction && (
            <Plot
              data={[
                {
                  x: predictedPrices.map((point) => point.time),
                  y: predictedPrices.map((point) => point.price),
                  type: 'scatter',
                  mode: 'lines+markers',
                  marker: { color: 'blue', size: 8 },
                  line: { shape: 'linear', color: 'blue' },
                  name: 'Predicted Prices',
                },
              ]}
              layout={{
                autosize: true,
                margin: { l: 40, r: 40, t: 40, b: 40 },
                xaxis: { title: 'Time', showgrid: true, automargin: true },
                yaxis: { title: 'Price', showgrid: true, automargin: true },
                orientation: 'h', // For horizontal layout
                hovermode: 'closest', // Enable hover on the nearest point
              }}

              config={{
                responsive: true,
                scrollZoom: true,
                // displayModeBar: false,
              }}
              style={{ width: '100%', height: "50vh" }}
            />
          )}
        </Col>
        </Row>

        <Row>
          <h3 className={styles.chartTitle}>EMA, EMA Slope, RSI, Bollinger Band, Macd</h3>
        </Row>
        <Row>
          <Col md={4}>
            {/* Dropdown for selecting models */}
            <Dropdown
              label="Select Currency To Predict (Model 2)"
              options={availableModels2.map((model) => ({
                label: model,
                value: model,
              }))}
              value={selectedModel2}
              onChange={setSelectedModel2}
            />
          </Col>
          <Col md={4}>
            {/* Dropdown for creating a new model */}
            <div className={styles.createModelSection}>
              <Dropdown
                label="Select New Currency Model to Generate"
                options={filteredCurrencyOptions2}
                value={currencyPair}
                onChange={(value) => setCurrencyPair(value)}
              />
            </div>
          </Col>
          <Col md={4} className={styles.createModelSection}>
            <button
              onClick={() => handleCreateModel2(currencyPair || filteredCurrencyOptions2[0]?.value)}
              disabled={loading || filteredCurrencyOptions2.length === 0}
            >
              {loading
                ? 'Creating Model...'
                : `Create Model 2 for ${currencyPair || filteredCurrencyOptions2[0]?.value}`}
            </button>
          </Col>
        </Row>
      </Row>
        <Row>
          <Col md={12}>
            {message && <p className={styles.message}>{message}</p>}
            {selectedModel2 && !loading && (
              <>
                <h3 className={styles.chartTitle}>Next 10-Minute Prediction for {currentModelForPrediction2}</h3>
                {error && <p className={styles.error}>Error: {error}</p>}
              </>
            )}
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            {/* Plotly Chart */}
            {predictedPrices2.length > 0 && currentModelForPrediction2 && (
              <Plot
                data={[
                  {
                    x: predictedPrices2.map((point) => point.time),
                    y: predictedPrices2.map((point) => point.price),
                    type: 'scatter',
                    mode: 'lines+markers',
                    marker: { color: 'blue', size: 8 },
                    line: { shape: 'linear', color: 'blue' },
                    name: 'Predicted Prices',
                  },
                ]}
                layout={{
                  autosize: true,
                  margin: { l: 40, r: 40, t: 40, b: 40 },
                  xaxis: { title: 'Time', showgrid: true, automargin: true },
                  yaxis: { title: 'Price', showgrid: true, automargin: true },
                  orientation: 'h', // For horizontal layout
                  hovermode: 'closest', // Enable hover on the nearest point
                }}

                config={{
                  responsive: true,
                  scrollZoom: true,
                  // displayModeBar: false,
                }}
                style={{ width: '100%', height: "50vh" }}
              />
            )}
          </Col>
        </Row>


      </Container>


    </>
  );
}

export default LSTMPredictChart;
